import {Button, Modal, Spinner} from 'react-bootstrap'
import {useMutation} from '@apollo/client'
import {useEffect} from 'react'
import {toast} from 'react-toastify'
import {DELETE_RESOURCE_CENTER} from '../../../gql/mutations/resourceCenterMutations'
import {IResourceCenter, IDeleteResourceCenterResponse} from '../../../types/resourceCenter'
import {parseId} from '../../../helpers'
import {getResourceTypeIcon, getResourceTypeBadgeStyle, RESOURCE_TYPE_OPTIONS} from '../resourceTypeHelpers'
import moment from 'moment'

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  resource: IResourceCenter
}

export const ModalDeleteResource: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  resource,
}) => {
  const [deleteResource, {data, loading, error}] = useMutation<IDeleteResourceCenterResponse>(
    DELETE_RESOURCE_CENTER
  )

  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error.message}`)
    }
  }, [error])

  useEffect(() => {
    if (!data) return

    if (data.deleteResourceCenter.success) {
      onClose()
      refetch()
      toast.success(data.deleteResourceCenter.message || 'Resource deleted successfully')
    } else if (
      data.deleteResourceCenter.errors &&
      data.deleteResourceCenter.errors.length > 0
    ) {
      toast.error(`Error: ${data.deleteResourceCenter.errors[0].message}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleDelete = async () => {
    try {
      await deleteResource({
        variables: {
          id: parseId(resource.id),
        },
      })
    } catch (err) {
      console.error('Error deleting resource:', err)
      toast.error('Error deleting resource')
    }
  }

  const fileName = resource.file?.split('/').pop()?.split('?')[0] || resource.url || 'Unknown resource'
  const fileExtension = resource.file ? (fileName.split('.').pop()?.toUpperCase() || 'FILE') : 'URL'

  return (
    <Modal show={isOpen} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton className="bg-danger text-white">
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-triangle-fill" /> Delete Resource
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="alert alert-warning mb-3">
          <i className="bi bi-exclamation-triangle me-2" />
          <strong>Warning:</strong> This action cannot be undone. The file will be permanently
          deleted from the server.
        </div>

        <p className="mb-3">Are you sure you want to delete this resource?</p>

        {/* Resource Info Card */}
        <div className="card bg-light">
          <div className="card-body">
            <div className="d-flex align-items-start gap-3 mb-3">
              <div
                style={{
                  width: 60,
                  height: 60,
                  fontSize: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fff',
                  borderRadius: 8,
                  border: '2px solid #dee2e6',
                }}
              >
                {getResourceTypeIcon(resource.resourceType)}
              </div>
              <div className="flex-fill">
                <h6 className="mb-2 fw-bold">{resource.name}</h6>
                <div className="mb-2">
                  <span style={getResourceTypeBadgeStyle(resource.resourceType)}>
                    {RESOURCE_TYPE_OPTIONS.find((opt) => opt.value === resource.resourceType)
                      ?.label || resource.resourceType}
                  </span>
                </div>
                {resource.description && (
                  <p className="text-muted small mb-2">{resource.description}</p>
                )}
              </div>
            </div>

            <hr />

            <div className="row small">
              <div className="col-6">
                <div className="mb-2">
                  <strong>File:</strong>
                  <div className="text-muted">{fileName}</div>
                </div>
                <div className="mb-2">
                  <strong>Type:</strong>
                  <div className="text-muted">{fileExtension}</div>
                </div>
              </div>
              <div className="col-6">
                <div className="mb-2">
                  <strong>Created:</strong>
                  <div className="text-muted">
                    {moment(resource.created).format('MMM DD, YYYY')}
                  </div>
                </div>
                <div className="mb-2">
                  <strong>Views:</strong>
                  <div className="text-muted">{resource.viewsCount} times</div>
                </div>
              </div>
            </div>

            {resource.user && (
              <>
                <hr />
                <div className="small">
                  <strong>Created by:</strong>{' '}
                  <span className="text-muted">
                    {resource.user.firstName} {resource.user.lastName}
                  </span>
                </div>
              </>
            )}

            {resource.documentTag && (
              <div className="small mt-2">
                <strong>Tag:</strong>{' '}
                <span className="badge bg-secondary">{resource.documentTag.tagName}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 p-3 border border-danger rounded bg-light">
          <div className="d-flex align-items-start gap-2">
            <i className="bi bi-info-circle text-danger mt-1" />
            <div className="small">
              <strong>What will be deleted:</strong>
              <ul className="mb-0 mt-1">
                <li>The database record for this resource</li>
                <li>
                  The physical file: <code>{fileName}</code>
                </li>
                <li>All metadata and view statistics</li>
              </ul>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDelete} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Deleting...
            </>
          ) : (
            <>
              <i className="bi bi-trash me-1" />
              Yes, Delete Resource
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
